import type { ReactNode } from 'react';
import { Tooltip, type DataTableColumn } from '@odo/shared-ui';
import { Avatar } from '../../../Avatar';
import { StatusPill } from '../../../patient-files/overview/shared';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { PriorityChip } from '../../../work-queue/priority/PriorityChip';
import { phaseColorOverride } from '../../../../utils/phaseColors';
import type { PracticePatientRow } from './patientStatusJoin';

export const PATIENTS_TD_PADDING = 'px-3 first:pl-5 last:pr-4';
export const PATIENTS_TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';

export type PatientsSortBy = 'patient';

// Plain functions, not components: a local component here would break this file's
// react-refresh boundary (same convention as callColumns.tsx's `menuItemsFor`).

// An unmatched phase/team is an empty taxonomy lookup, so it renders an empty cell rather
// than a guessed phase (the PracticeStatCards/OpenCallsCell convention).
function phaseCell(phase: string | null): ReactNode {
    if (!phase) return null;
    return (
        <StatusPill
            value={phase}
            className="max-w-full"
            colorOverride={phaseColorOverride(phase)}
        />
    );
}

// A null label means no internalCaseStatus at all (StatusPill's "Not set"); otherwise the
// catalog's display string, coloured by phase like the Work Queue.
function internalStepCell(phase: string | null, label: string | null): ReactNode {
    if (!label) return <StatusPill value={null} className="max-w-full" />;
    return (
        <StatusPill
            value={label}
            className="max-w-full"
            colorOverride={phaseColorOverride(phase)}
        />
    );
}

// Only Patient sorts (server `sort: 'patient'`); the other columns are short chips, so the
// old blanket per-cell `overflow-hidden` is dropped.
export function buildPatientColumns(): DataTableColumn<PracticePatientRow, PatientsSortBy>[] {
    return [
        {
            key: 'patient',
            label: 'Patient',
            width: 'auto',
            sortable: true,
            sortKey: 'patient',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => (
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 flex-shrink-0">
                        <Avatar
                            user={{ name: patient.name, email: '' }}
                            thumbnailURL={patient.profileThumbnailUrl ?? undefined}
                        />
                    </div>
                    <Tooltip content={patient.name}>
                        <span className="block truncate font-semibold text-gray-900 min-w-0">
                            {patient.name}
                        </span>
                    </Tooltip>
                </div>
            )
        },
        {
            key: 'priority',
            label: 'Priority',
            width: 'auto',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => <PriorityChip priority={patient.priority} />
        },
        {
            key: 'phase',
            label: 'Phase',
            width: 'auto',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => phaseCell(patient.phase)
        },
        {
            key: 'internalStep',
            label: 'Internal step',
            width: 'auto',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => internalStepCell(patient.phase, patient.internalStepLabel)
        },
        {
            key: 'externalStep',
            label: 'External step',
            width: 'auto',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => <StatusPill value={patient.externalStep} className="max-w-full" />
        },
        {
            key: 'team',
            label: 'Team',
            width: 'auto',
            cellClassName: PATIENTS_TD_PADDING,
            headerClassName: PATIENTS_TH_PADDING,
            render: (patient) => patient.team && <TeamPill team={patient.team} size="xs" />
        }
    ];
}
